<!-- In-cluster inference pipelines, listed alongside the App Engine tasks on #/apps.

     These are NOT App Engine tasks: they have no descriptor zip and do not come back from
     Task.fetchAll(), because a multi-hour whole-slide CPU job is run by a queued worker
     rather than handed a file through /inputs. They still belong on this page — it is the
     inventory of what can be run — so they are fetched separately and rendered here.

     This is also where the pipeline's settings live, because this page is where someone
     looking for "how is this pipeline configured" actually goes. -->
<template>
  <div class="panel">
    <p class="panel-heading">{{ $t('pipelines-inference-heading') }}</p>

    <section v-if="!pipelines.length" class="panel-block">
      <em class="has-text-grey">{{ $t('no-result') }}</em>
    </section>
    <section v-else class="panel-block lower-section-flex">
      <div v-for="p in pipelines" :key="p.key" class="card infer-card">
        <div class="card-content">
          <div class="media">
            <div class="media-content">
              <p class="title is-4 less-bottom">{{ $t(p.labelKey) }}</p>
              <time>{{ p.model || p.key }}</time>
            </div>
            <div class="media-right">
              <b-tag type="is-success" v-if="p.enabled">{{ $t('pipelines-inference-ready') }}</b-tag>
              <b-tag type="is-warning" v-else>{{ $t('pipelines-inference-unavailable') }}</b-tag>
            </div>
          </div>

          <div class="content">
            {{ $t('pipelines-inference-description') }}
          </div>

          <hr class="infer-rule">

          <template v-if="p.hasSettings">
            <b-field :label="$t('pipelines-threshold')">
              <div class="threshold-row">
                <b-slider
                  v-model="pct[p.key]"
                  :min="30" :max="95" :step="5"
                  :tooltip="false"
                  :disabled="!p.enabled"
                  ticks
                  class="threshold-slider"
                />
                <span class="threshold-value">{{ pct[p.key] }}%</span>
              </div>
            </b-field>
            <p class="help threshold-help">{{ $t('pipelines-threshold-help') }}</p>

            <b-button
              type="is-link"
              expanded
              :loading="saving === p.key"
              :disabled="!p.enabled || pct[p.key] === saved[p.key]"
              @click="save(p)"
            >
              {{ $t('pipelines-threshold-save') }}
            </b-button>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import pipelineService from '@/api/pipeline-service';

export default {
  name: 'InferencePipelinePanel',
  data() {
    return {
      pipelines: [],
      pct: {},
      saved: {},
      saving: null,
    };
  },
  methods: {
    async load() {
      try {
        let {data} = await pipelineService.getCatalog();
        // Only the pipelines this platform actually runs belong on this panel; the
        // App Engine tasks are listed by AppListPanel below it.
        this.pipelines = (data.pipelines || []).filter(p => p.kind === 'inference');
        this.pipelines.forEach(p => {
          let v = Math.round((p.insertionThreshold != null ? p.insertionThreshold : 0.5) * 100);
          this.$set(this.pct, p.key, v);
          this.$set(this.saved, p.key, v);
        });
      } catch (error) {
        console.log(error);
        this.pipelines = [];
      }
    },
    async save(p) {
      this.saving = p.key;
      try {
        await pipelineService.savePipelineSettings(p.key, this.pct[p.key] / 100);
        this.$set(this.saved, p.key, this.pct[p.key]);
        this.$notify({
          type: 'success',
          text: this.$t('pipelines-threshold-saved', {pct: this.pct[p.key]})
        });
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('pipelines-notif-error')});
      } finally {
        this.saving = null;
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.lower-section-flex {
  display: flex;
  flex-direction: row;
  gap: 1%;
  flex-wrap: wrap;
}

.panel-block {
  padding-top: 0.8em;
}

.infer-card {
  border: 1px solid #e5e8ee;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 17, 22, 0.04);
  margin: 1em;
  flex-basis: 26rem;
  max-width: 100%;
}

.less-bottom {
  margin-bottom: 0.9rem !important;
}

.infer-rule {
  margin: 1rem 0 0.75rem;
  background-color: #eef0f4;
  height: 1px;
}

.threshold-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.threshold-slider {
  flex: 1;
}

.threshold-value {
  min-width: 3.2rem;
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.threshold-help {
  margin-top: -0.25rem;
  margin-bottom: 1rem;
}
</style>
